import { Typography } from "antd";
import LegalPageLayout from "./LegalPageLayout";

const { Title, Paragraph, Text } = Typography;

export default function PrivacyPolicyPage() {
  return (
    <LegalPageLayout title="Privacy Policy" lastUpdated="October 22, 2026">
      <Paragraph>
        This Privacy Policy explains how Hairplane ("Hairplane," "we," "us," or "our") collects, uses, shares, and
        protects information in connection with our salon and barbershop management platform (the "Service"),
        including our website, booking pages, and client-facing app.
      </Paragraph>

      <Title level={4}>1. Who This Policy Covers</Title>
      <Paragraph>
        Hairplane is used by salon and barbershop businesses ("Business Customers") to manage their operations,
        and by those businesses' own clients ("Clients") to book appointments and communicate with the business.
        This policy covers both groups. For personal information Clients provide through a Business Customer's use
        of the Service, the Business Customer is the data controller and Hairplane acts as a service provider /
        processor on their behalf.
      </Paragraph>

      <Title level={4}>2. Information We Collect</Title>
      <Paragraph>
        <Text strong>Account information:</Text> name, email, phone number, business name and address, and
        password (stored as a secure hash, never in plain text), for Business Customers and their staff.
      </Paragraph>
      <Paragraph>
        <Text strong>Client information:</Text> name, phone number, email, address, appointment history, notes,
        and preferences, as entered by a Business Customer or provided directly by a Client through a booking page
        or the client app.
      </Paragraph>
      <Paragraph>
        <Text strong>Payment-related information:</Text> we do not store full payment card numbers. Payments are
        processed by our third-party payment processor, who handles and stores cardholder data under their own
        security standards (PCI-DSS). We retain only limited information such as the last four digits of a card
        and transaction records.
      </Paragraph>
      <Paragraph>
        <Text strong>Job applicant information:</Text> if a Business Customer uses our hiring tools, applicant
        name, contact information, and application materials submitted through that feature.
      </Paragraph>
      <Paragraph>
        <Text strong>Usage information:</Text> standard technical data such as IP address, browser type, device
        information, and pages visited, collected automatically when you use the Service.
      </Paragraph>

      <Title level={4}>3. How We Use Information</Title>
      <Paragraph>
        We use the information above to: provide and operate the Service (scheduling, payments, client records,
        staff management); send transactional communications (booking confirmations, reminders, verification
        codes); send marketing communications where permitted and consented to; improve and secure the Service;
        and comply with legal obligations.
      </Paragraph>

      <Title level={4}>4. Text Messaging (SMS)</Title>
      <Paragraph>
        If you provide a phone number and consent to receive text messages, we (or a Business Customer using our
        platform) may send you appointment reminders, waitlist notifications, login codes, or promotional messages.
        Message frequency varies. Message and data rates may apply. You can opt out of non-essential messages at
        any time by replying <Text code>STOP</Text>, or reply <Text code>HELP</Text> for assistance. Carriers are
        not liable for delayed or undelivered messages. Consent to receive marketing text messages is not a
        condition of using the Service.
      </Paragraph>
      <Paragraph>
        No mobile information will be shared with third parties or affiliates for marketing or promotional
        purposes. Text messaging opt-in data and consent are not shared with any third parties, except the
        service providers that deliver our messages.
      </Paragraph>

      <Title level={4}>5. Email Communications</Title>
      <Paragraph>
        Marketing emails include a working unsubscribe link, which we honor promptly. Transactional emails
        (receipts, verification codes, account notices) are sent as part of operating the Service and are not
        subject to the same opt-out, as they're not promotional in nature.
      </Paragraph>

      <Title level={4}>6. How We Share Information</Title>
      <Paragraph>
        We do not sell personal information. We share information with: service providers who help us operate the
        Service (cloud hosting, email and SMS delivery, payment processing); a Business Customer, with respect to
        their own Clients' information entered into the Service; and others where required by law or to protect
        rights, safety, or property.
      </Paragraph>

      <Title level={4}>7. Data Retention</Title>
      <Paragraph>
        We retain information for as long as needed to provide the Service, comply with legal obligations, resolve
        disputes, and enforce our agreements. A Business Customer may request deletion of their account and
        associated Client data, subject to records we're required to retain by law.
      </Paragraph>

      <Title level={4}>8. Data Security</Title>
      <Paragraph>
        We use industry-standard safeguards, including encrypted connections, hashed passwords, and access
        controls, to protect information. No method of transmission or storage is completely secure, and we
        cannot guarantee absolute security.
      </Paragraph>

      <Title level={4}>9. Your Choices and Rights</Title>
      <Paragraph>
        Depending on where you live, you may have rights to access, correct, or delete your personal information,
        or to opt out of certain uses. To exercise these rights, contact us using the information below, or contact
        the salon/barbershop business directly if the information in question is held on their behalf.
      </Paragraph>

      <Title level={4}>10. Children's Privacy</Title>
      <Paragraph>
        The Service is not directed to children under 13, and we do not knowingly collect personal information
        from children under 13.
      </Paragraph>

      <Title level={4}>11. Changes to This Policy</Title>
      <Paragraph>
        We may update this policy from time to time. We'll update the "Last updated" date above, and for material
        changes, provide additional notice where appropriate.
      </Paragraph>

      <Title level={4}>12. Contact Us</Title>
      <Paragraph>
        Questions about this policy can be sent to <Text code>privacy@hairplane.com</Text>.
      </Paragraph>
    </LegalPageLayout>
  );
}
